import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { 
  ChevronDown, ChevronRight, AlertCircle, 
  TrendingUp, TrendingDown, Calendar, Building2,
  MessageSquare, Euro, Utensils, Users, X, Lightbulb,
  PieChart, Wine, Coffee, Search, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Formatage des nombres en Euro
const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return new Intl.NumberFormat('fr-FR', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Formatage en pourcentage
const formatPercent = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return `${value.toFixed(1)}%`;
};

// Déterminer le type de section pour les couleurs
const getSectionType = (key) => {
  if (key.includes('ca_') || key.includes('_ca') || key.startsWith('ca_') || key.includes('chiffre')) return 'revenue';
  if (key.includes('cout_') || key.includes('_cout') || key.includes('_achats') || key.includes('_charges') || key === 'consommations' || key === 'personnel' || key === 'taxes' || key === 'amortissements' || key === 'matieres' || key === 'resultat_financier' || key === 'resultat_exceptionnel') return 'cost';
  if (key.includes('_marge') || key === 'marge_commerciale' || key === 'ebe' || key === 'resultat_courant' || key === 'resultat_exploitation' || key === 'resultat_exercice') return 'result';
  return 'neutral';
};

// Vérifier si c'est une section de résultat (style jaune)
const isResultSection = (key) => {
  return key.includes('_marge') || key === 'marge_commerciale' || key === 'ebe' || key === 'resultat_exercice' || key === 'resultat_exploitation';
};

// Sections cachées
const hiddenSections = ['valeur_ajoutee', 'marge_production', 'resultat_courant', 'ca_hotel', 'cout_matieres'];

// Sections cachées si = 0
const hideIfZeroSections = ['resultat_exceptionnel', 'taxes', 'amortissements', 'resultat_financier'];

// Sections avec valeurs négatives
const negativeValueSections = ['amortissements', 'resultat_courant', 'resultat_exercice', 'resultat_exploitation', 'resultat_financier'];

// Badge d'évolution - Responsive
const EvolutionBadge = ({ current, previous, isExpense = false, isNegativeValue = false, isDesktop = false }) => {
  if (!previous || previous === 0 || current === null || current === undefined) return null;
  
  const evolution = ((current - previous) / Math.abs(previous)) * 100;
  const isZero = Math.abs(evolution) < 0.5;
  
  let isGood;
  if (isNegativeValue) {
    isGood = evolution > 0;
  } else if (isExpense) {
    isGood = evolution < 0;
  } else {
    isGood = evolution > 0;
  }
  
  const colorClasses = isZero 
    ? 'text-gray-400'
    : isGood 
      ? 'text-emerald-600' 
      : 'text-red-500';
  
  const isUp = evolution > 0;
  
  const fmtN1 = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(previous);
  
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${colorClasses} ${isDesktop ? 'text-xs' : 'text-[10px]'}`} title="vs N-1">
      {isZero ? (
        <span>=</span>
      ) : isUp ? (
        <>
          <TrendingUp size={isDesktop ? 14 : 10} strokeWidth={2.5} />
          <span>+{evolution.toFixed(0)}%</span>
        </>
      ) : (
        <>
          <TrendingDown size={isDesktop ? 14 : 10} strokeWidth={2.5} />
          <span>{evolution.toFixed(0)}%</span>
        </>
      )}
      <span className={`font-normal opacity-60 ${isDesktop ? 'text-[10px]' : 'text-[9px]'} ml-0.5`}>({fmtN1})</span>
    </span>
  );
};

// Composant pour les ratios détaillés de la marge restaurant
const MargeRatiosDetail = ({ allSections, selectedYear }) => {
  const caSection = allSections?.find(s => s.key === 'ca_restaurant');
  const coutSection = allSections?.find(s => s.key === 'cout_marchandises');
  
  if (!caSection || !coutSection) return null;
  
  const getItemValue = (items, labels, year) => {
    for (const label of labels) {
      const item = items?.find(i => i.label?.toLowerCase().includes(label.toLowerCase()));
      if (item) {
        return item.yearly?.[year] || item.cumul?.[year] || 0;
      }
    }
    return 0;
  };
  
  const caFood = getItemValue(caSection.items, ['FOOD', 'Nourriture'], selectedYear);
  const caSoft = getItemValue(caSection.items, ['SOFT', 'NON ALCOOL'], selectedYear);
  const caAlcool = getItemValue(caSection.items, ['ALCOOL'], selectedYear);
  
  const achatFood = getItemValue(coutSection.items, ['ACHATS FOOD', 'Achats nourriture'], selectedYear);
  const achatSoft = getItemValue(coutSection.items, ['SOFT', 'NON ALCOOL'], selectedYear);
  const achatAlcool = getItemValue(coutSection.items, ['ALCOOLS', 'Achats alcool'], selectedYear);
  
  const margeFood = caFood - achatFood;
  const margeSoft = caSoft - achatSoft;
  const margeAlcool = caAlcool - achatAlcool;
  
  const ratioFood = caFood > 0 ? (margeFood / caFood) * 100 : 0;
  const ratioSoft = caSoft > 0 ? (margeSoft / caSoft) * 100 : 0;
  const ratioAlcool = caAlcool > 0 ? (margeAlcool / caAlcool) * 100 : 0;
  
  const categories = [
    { name: 'Nourriture', icon: Utensils, ca: caFood, cout: achatFood, marge: margeFood, ratio: ratioFood, color: 'text-orange-600', bgColor: 'bg-orange-50' },
    { name: 'Boissons Non Alcool', icon: Coffee, ca: caSoft, cout: achatSoft, marge: margeSoft, ratio: ratioSoft, color: 'text-blue-600', bgColor: 'bg-blue-50' },
    { name: 'Alcool', icon: Wine, ca: caAlcool, cout: achatAlcool, marge: margeAlcool, ratio: ratioAlcool, color: 'text-purple-600', bgColor: 'bg-purple-50' }
  ];
  
  return (
    <div className="px-4 pb-3 pt-2 space-y-2 bg-amber-50/30">
      <div className="flex items-center gap-2 text-xs text-amber-700 font-medium">
        <PieChart size={14} />
        Détail des ratios par catégorie
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {categories.map((cat) => {
          const Icon = cat.icon;
          return (
            <div key={cat.name} className={`${cat.bgColor} rounded-lg p-2 border border-neutral-200`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Icon size={12} className={cat.color} />
                <span className={`font-medium text-[10px] ${cat.color}`}>{cat.name}</span>
              </div>
              <div className="space-y-0.5 text-[10px]">
                <div className="flex justify-between">
                  <span className="text-neutral-500">CA</span>
                  <span className="font-medium text-neutral-700">{formatCurrency(cat.ca)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Achats</span>
                  <span className="font-medium text-red-600">-{formatCurrency(cat.cout)}</span>
                </div>
                <div className="flex justify-between border-t border-neutral-200 pt-0.5 mt-0.5">
                  <span className="text-neutral-600 font-medium">Marge</span>
                  <span className={`font-bold ${cat.marge >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(cat.marge)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Ratio</span>
                  <span className={`font-bold ${cat.ratio >= 50 ? 'text-emerald-600' : cat.ratio >= 30 ? 'text-amber-600' : 'text-red-600'}`}>
                    {formatPercent(cat.ratio)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="bg-neutral-100 rounded-lg p-2 border border-neutral-200">
        <div className="flex items-center justify-between text-[10px]">
          <span className="font-medium text-neutral-700">Total Marge Commerciale</span>
          <div className="flex items-center gap-3">
            <span className="text-neutral-600">
              Ratio: <span className="font-bold text-amber-600">
                {formatPercent((ratioFood * caFood + ratioSoft * caSoft + ratioAlcool * caAlcool) / (caFood + caSoft + caAlcool))}
              </span>
            </span>
            <span className="font-bold text-emerald-600">{formatCurrency(margeFood + margeSoft + margeAlcool)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant Section Row - Design responsive
const SectionAccordion = ({ section, selectedYear, selectedMonth, hideZeros, comments, onOpenComment, isGolf = true, allSections = [], isDesktop = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showN1Comparison, setShowN1Comparison] = useState(false);
  
  const sectionType = getSectionType(section.key);
  const isResult = isResultSection(section.key);
  const isExpenseSection = sectionType === 'cost';
  const isNegativeValue = negativeValueSections.includes(section.key);
  const isMargeCommerciale = section.key === 'marge_commerciale';
  
  // Calcul du total
  const sectionTotal = useMemo(() => {
    if (section.subtotal) {
      if (isGolf && selectedMonth !== 'cumul') {
        return section.subtotal.monthly?.[`${selectedYear}-${selectedMonth}`] || 0;
      }
      return section.subtotal.yearly?.[selectedYear] || section.subtotal.cumul?.[selectedYear] || 0;
    }
    return null;
  }, [section, selectedYear, selectedMonth, isGolf]);
  
  // Valeur N-1
  const prevYear = String(parseInt(selectedYear) - 1);
  const prevYearTotal = useMemo(() => {
    if (!section.subtotal) return null;
    if (isGolf && selectedMonth !== 'cumul') {
      return section.subtotal.monthly?.[`${prevYear}-${selectedMonth}`] || 0;
    }
    return section.subtotal.yearly?.[prevYear] || section.subtotal.cumul?.[prevYear] || 0;
  }, [section, prevYear, selectedMonth, isGolf]);
  
  // Items visibles
  const visibleItems = useMemo(() => {
    if (!hideZeros) return section.items || [];
    return (section.items || []).filter(item => {
      const value = isGolf && selectedMonth !== 'cumul'
        ? item.monthly?.[`${selectedYear}-${selectedMonth}`]
        : (item.yearly?.[selectedYear] || item.cumul?.[selectedYear]);
      return value && Math.abs(value) > 0.01;
    });
  }, [section.items, hideZeros, selectedYear, selectedMonth, isGolf]);

  // Cacher si zéro
  if (hideZeros && sectionTotal !== null && Math.abs(sectionTotal) < 0.01) return null;
  if (hideIfZeroSections.includes(section.key) && (sectionTotal === null || Math.abs(sectionTotal) < 0.01)) return null;

  const commentKey = `${section.key}_${selectedMonth === 'cumul' ? `${selectedYear}-cumul` : `${selectedYear}-${selectedMonth}`}`;
  const currentComment = comments?.[commentKey];
  
  // Indicateur de type
  const getTypeIndicator = () => {
    if (sectionType === 'revenue') return 'bg-emerald-500';
    if (sectionType === 'cost') return 'bg-red-500';
    if (isResult) return 'bg-amber-500';
    return 'bg-gray-300';
  };

  // Couleur de la valeur
  const getValueColor = () => {
    if (sectionType === 'revenue') return 'text-emerald-600';
    if (sectionType === 'cost') return 'text-red-600';
    if (sectionType === 'result') return sectionTotal >= 0 ? 'text-emerald-600' : 'text-red-600';
    return 'text-gray-900';
  };

  const handleClick = () => {
    if (isResult || isMargeCommerciale) {
      setShowN1Comparison(!showN1Comparison);
    } else if (visibleItems.length > 0) {
      setIsExpanded(!isExpanded);
    }
  };

  // Sections Résultat/Marge : Design responsive
  if (isResult || section.is_ebe) {
    return (
      <div className="border-t border-gray-50 first:border-t-0">
        <div 
          onClick={handleClick}
          className={`flex items-center bg-amber-50/70 cursor-pointer hover:bg-amber-100/50 transition-colors ${isDesktop ? 'py-3 px-4' : 'py-2 px-2'}`}
          data-testid={`section-${section.key}`}
        >
          <div className="flex items-center gap-1.5 mr-2">
            <div className={`${isDesktop ? 'w-2 h-2' : 'w-1.5 h-1.5'} rounded-full bg-amber-500`} />
            {showN1Comparison ? (
              <ChevronDown size={isDesktop ? 16 : 12} className="text-amber-600" />
            ) : (
              <ChevronRight size={isDesktop ? 16 : 12} className="text-amber-600" />
            )}
          </div>
          <span className={`flex-1 font-semibold text-amber-800 truncate ${isDesktop ? 'text-sm' : 'text-xs'}`}>{section.title}</span>
          <div className={`${isDesktop ? 'w-24' : 'w-14'} flex justify-end`}>
            {selectedYear !== '2023' && (
              <EvolutionBadge current={sectionTotal} previous={prevYearTotal} isExpense={false} isNegativeValue={isNegativeValue} isDesktop={isDesktop} />
            )}
          </div>
          <span className={`${isDesktop ? 'w-32 text-lg' : 'w-20 text-sm'} text-right font-bold tabular-nums ${sectionTotal >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatCurrency(sectionTotal)}
          </span>
          {onOpenComment && (
            <button
              onClick={(e) => { e.stopPropagation(); onOpenComment(section.key, section.title, commentKey, currentComment?.text); }}
              className={`ml-2 ${isDesktop ? 'p-1.5' : 'p-0.5'} rounded transition-colors ${currentComment ? 'text-amber-600 bg-amber-100' : 'text-gray-300 hover:text-amber-500'}`}
              title={currentComment ? "Voir le commentaire" : "Ajouter un commentaire"}
            >
              <MessageSquare size={isDesktop ? 14 : 10} />
            </button>
          )}
        </div>
        
        {showN1Comparison && selectedYear !== '2023' && prevYearTotal !== null && (
          <div className={`${isDesktop ? 'px-10 py-2' : 'px-6 py-1'} bg-amber-50/40 border-t border-amber-100/50`}>
            <div className={`flex items-center justify-between ${isDesktop ? 'text-xs' : 'text-[10px]'}`}>
              <span className="text-amber-600/70">vs N-1</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 tabular-nums">{formatCurrency(prevYearTotal)}</span>
                <span className={`font-semibold tabular-nums ${(sectionTotal - prevYearTotal) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {(sectionTotal - prevYearTotal) >= 0 ? '+' : ''}{formatCurrency(sectionTotal - prevYearTotal)}
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Ratios détaillés pour Marge Brute Commerciale Restaurant */}
        {isMargeCommerciale && showN1Comparison && !isGolf && allSections.length > 0 && (
          <MargeRatiosDetail allSections={allSections} selectedYear={selectedYear} />
        )}
      </div>
    );
  }

  // Sections normales - Responsive
  return (
    <div className="group border-t border-gray-50 first:border-t-0" data-testid={`section-${section.key}`}>
      <div
        onClick={handleClick}
        className={`flex items-center transition-all ${isDesktop ? 'py-2.5 px-4' : 'py-1 px-2'} ${
          visibleItems.length > 0 ? 'cursor-pointer hover:bg-gray-50/80' : ''
        }`}
      >
        <div className={`flex items-center ${isDesktop ? 'gap-2 mr-3' : 'gap-1 mr-1.5'}`}>
          <div className={`${isDesktop ? 'w-1.5 h-1.5' : 'w-1 h-1'} rounded-full ${getTypeIndicator()}`} />
          {visibleItems.length > 0 ? (
            isExpanded ? (
              <ChevronDown size={isDesktop ? 14 : 10} className="text-gray-400" />
            ) : (
              <ChevronRight size={isDesktop ? 14 : 10} className="text-gray-400" />
            )
          ) : (
            <div className={isDesktop ? 'w-[14px]' : 'w-[10px]'} />
          )}
        </div>
        
        <span className={`flex-1 text-gray-700 font-medium truncate ${isDesktop ? 'text-sm' : 'text-[11px]'}`}>{section.title}</span>
        
        <div className={`${isDesktop ? 'w-24' : 'w-14'} flex justify-end`}>
          {selectedYear !== '2023' && (
            <EvolutionBadge current={sectionTotal} previous={prevYearTotal} isExpense={isExpenseSection} isNegativeValue={isNegativeValue} isDesktop={isDesktop} />
          )}
        </div>
        
        <span className={`${isDesktop ? 'w-32 text-base' : 'w-20 text-xs'} text-right font-semibold tabular-nums ${getValueColor()}`}>
          {formatCurrency(sectionTotal)}
        </span>
        
        {onOpenComment && (
          <button
            onClick={(e) => { e.stopPropagation(); onOpenComment(section.key, section.title, commentKey, currentComment?.text); }}
            className={`ml-2 ${isDesktop ? 'p-1.5' : 'p-0.5'} rounded transition-colors ${currentComment ? 'text-blue-500' : 'text-gray-200 hover:text-gray-400'}`}
            title={currentComment ? "Voir le commentaire" : "Ajouter un commentaire"}
          >
            <MessageSquare size={isDesktop ? 14 : 10} />
          </button>
        )}
      </div>
      
      {isExpanded && visibleItems.length > 0 && (
        <div className={`${isDesktop ? 'ml-8 pl-4 py-2' : 'ml-5 pl-2 py-0.5'} border-l border-gray-100`}>
          <table className={`w-full ${isDesktop ? 'text-xs' : 'text-[10px]'}`}>
            <thead>
              <tr className="text-gray-400">
                <th className="text-left py-0.5 font-medium">Libellé</th>
                <th className={`text-right py-0.5 font-medium ${isDesktop ? 'w-28' : 'w-16'}`}>{selectedYear}</th>
                {selectedYear !== '2023' && <th className={`text-right py-0.5 font-medium ${isDesktop ? 'w-24' : 'w-14'}`}>N-1</th>}
                <th className={`text-center py-0.5 font-medium ${isDesktop ? 'w-20' : 'w-10'}`}>vs N-1</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((item, idx) => {
                const currentValue = isGolf && selectedMonth !== 'cumul'
                  ? item.monthly?.[`${selectedYear}-${selectedMonth}`]
                  : (item.yearly?.[selectedYear] || item.cumul?.[selectedYear]);
                const prevValue = isGolf && selectedMonth !== 'cumul'
                  ? item.monthly?.[`${prevYear}-${selectedMonth}`]
                  : (item.yearly?.[prevYear] || item.cumul?.[prevYear]);

                return (
                  <tr key={idx} className="border-t border-gray-50">
                    <td className={`text-gray-600 ${isDesktop ? 'py-1.5' : 'py-0.5'}`}>{item.label}</td>
                    <td className={`text-right tabular-nums font-medium ${getValueColor()} ${isDesktop ? 'py-1.5' : 'py-0.5'}`}>
                      {formatCurrency(currentValue)}
                    </td>
                    {selectedYear !== '2023' && (
                      <td className={`text-right text-gray-400 tabular-nums ${isDesktop ? 'py-1.5' : 'py-0.5'}`}>
                        {formatCurrency(prevValue)}
                      </td>
                    )}
                    <td className={`text-center ${isDesktop ? 'py-1.5' : 'py-0.5'}`}>
                      <EvolutionBadge current={currentValue} previous={prevValue} isExpense={isExpenseSection} isNegativeValue={isNegativeValue} isDesktop={isDesktop} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Composant pour grouper les sections - Design responsive
const SectionGroup = ({ title, icon, sections, isDesktop = false, ...props }) => {
  return (
    <div className={isDesktop ? 'mb-6' : 'mb-3'}>
      <div className={`flex items-center gap-1 mb-1 ${isDesktop ? 'px-4' : 'px-2'}`}>
        <div className="text-gray-400">{icon}</div>
        <h3 className={`flex-1 font-bold text-gray-400 uppercase tracking-wider ${isDesktop ? 'text-xs' : 'text-[9px]'}`}>{title}</h3>
        <span className={`${isDesktop ? 'w-24 text-xs' : 'w-14 text-[9px]'} text-center text-gray-400 font-medium`}>vs N-1</span>
        <span className={`${isDesktop ? 'w-32 text-xs' : 'w-20 text-[9px]'} text-right text-gray-400 font-medium`}>Montant</span>
        <span className={isDesktop ? 'w-8' : 'w-5'}></span>
      </div>
      <div className={`bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden ${isDesktop ? 'rounded-xl' : ''}`}>
        {sections.map((section) => (
          <SectionAccordion
            key={section.key}
            section={section}
            isDesktop={isDesktop}
            {...props}
          />
        ))}
      </div>
    </div>
  );
};

// Composant pour les salaires - Design Dashboard Responsive
// Options de catégories pour le filtre
const CATEGORIES_BOSS = [
  { value: 'admin', label: 'Admin' },
  { value: 'golf', label: 'Golf' },
  { value: 'academie', label: 'Académie' },
  { value: 'restaurant', label: 'Restaurant' }
];

// Couleurs par catégorie
const CATEGORY_COLORS_BOSS = {
  admin: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-500' },
  golf: { bg: 'bg-green-100', text: 'text-green-600', dot: 'bg-green-500' },
  academie: { bg: 'bg-purple-100', text: 'text-purple-600', dot: 'bg-purple-500' },
  restaurant: { bg: 'bg-orange-100', text: 'text-orange-600', dot: 'bg-orange-500' }
};

// Composant pour les salaires - Design Dashboard simple avec filtre
const SalairesView = ({ accessToken, isDesktop = false }) => {
  const [postes, setPostes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  
  useEffect(() => {
    const fetchPostes = async () => {
      try {
        const res = await fetch(`${API_URL}/api/salaires/boss?token=${accessToken}`);
        if (res.ok) {
          const data = await res.json();
          setPostes(data.postes || []);
        }
      } catch (err) {
        console.error('Error fetching salaires:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPostes();
  }, [accessToken]);
  
  // Filtrer les postes par catégorie
  const filteredPostes = useMemo(() => {
    if (filterCategory === 'all') return postes;
    // Gérer les cas où categorie est null, undefined, 'None' ou vide
    return postes.filter(p => {
      const cat = (p.categorie || '').toLowerCase();
      return cat === filterCategory.toLowerCase();
    });
  }, [postes, filterCategory]);
  
  // Calculer les totaux (basé sur les postes filtrés)
  const totaux = useMemo(() => {
    return filteredPostes.reduce((acc, p) => ({
      brut: acc.brut + (p.salaire_brut || 0),
      global: acc.global + (p.cout_global || 0),
      count: acc.count + 1
    }), { brut: 0, global: 0, count: 0 });
  }, [filteredPostes]);

  // Obtenir le label de catégorie
  const getCategoryLabel = (cat) => {
    if (!cat || cat === 'None' || cat === 'null') return 'Non défini';
    const found = CATEGORIES_BOSS.find(c => c.value.toLowerCase() === cat.toLowerCase());
    return found?.label || cat;
  };
  
  // Obtenir les couleurs de catégorie
  const getCategoryColors = (cat) => {
    if (!cat || cat === 'None' || cat === 'null') {
      return { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };
    }
    return CATEGORY_COLORS_BOSS[cat.toLowerCase()] || { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };
  };
  
  if (loading) return <div className="text-center py-8 text-neutral-500">Chargement...</div>;

  return (
    <div>
      {/* Header avec filtre */}
      <div className={`flex items-center justify-between ${isDesktop ? 'mb-4' : 'mb-3'}`}>
        <div>
          <p className={`text-neutral-500 ${isDesktop ? 'text-sm' : 'text-xs'}`}>
            {totaux.count} collaborateur{totaux.count > 1 ? 's' : ''} • Coût global: {formatCurrency(totaux.global)}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className={`bg-white border border-gray-200 rounded font-medium shadow-sm ${isDesktop ? 'px-3 py-2 text-sm' : 'px-2 py-1.5 text-xs'}`}
          >
            <option value="all">Toutes catégories</option>
            {CATEGORIES_BOSS.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Liste vide */}
      {filteredPostes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-neutral-200">
          <Users size={48} className="mx-auto text-neutral-300 mb-3" />
          <h3 className="text-base font-semibold text-neutral-700 mb-1">
            {filterCategory === 'all' ? 'Aucun poste' : `Aucun poste dans ${getCategoryLabel(filterCategory)}`}
          </h3>
        </div>
      ) : (
        /* Liste des employés */
        <div className={`bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden ${isDesktop ? 'rounded-xl' : ''}`}>
          {/* En-tête de la liste */}
          <div className={`flex items-center bg-gray-50 border-b border-gray-100 font-medium text-gray-500 ${isDesktop ? 'px-4 py-3 text-xs' : 'px-2 py-2 text-[10px]'}`}>
            <div className="flex-1">Nom</div>
            <div className={isDesktop ? 'w-24' : 'w-16'}>Catégorie</div>
            <div className={`text-right ${isDesktop ? 'w-24' : 'w-16'}`}>Brut</div>
            <div className={`text-right ${isDesktop ? 'w-28' : 'w-20'}`}>Coût global</div>
            <div className={`text-right ${isDesktop ? 'w-20' : 'w-14'}`}>Heures</div>
          </div>
          
          {/* Liste des employés */}
          {filteredPostes.map((poste) => {
            return (
              <div 
                key={poste.id} 
                className={`flex items-center border-b border-gray-50 last:border-b-0 hover:bg-gray-50/50 ${isDesktop ? 'px-4 py-3' : 'px-2 py-2'}`}
              >
                {/* Nom et poste */}
                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-gray-800 truncate ${isDesktop ? 'text-sm' : 'text-[11px]'}`}>
                    {poste.nom} {poste.prenom}
                  </div>
                  <div className={`text-gray-400 truncate ${isDesktop ? 'text-xs' : 'text-[9px]'}`}>
                    {poste.poste || '-'}
                  </div>
                </div>
                
                {/* Badge catégorie */}
                <div className={isDesktop ? 'w-24' : 'w-16'}>
                  {(() => {
                    const colors = getCategoryColors(poste.categorie);
                    return (
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${colors.bg} ${colors.text} ${isDesktop ? 'text-[10px]' : 'text-[8px]'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`}></span>
                        {getCategoryLabel(poste.categorie)}
                      </span>
                    );
                  })()}
                </div>
                
                {/* Salaires */}
                <div className={`text-right text-gray-500 tabular-nums ${isDesktop ? 'w-24 text-xs' : 'w-16 text-[10px]'}`}>
                  {formatCurrency(poste.salaire_brut)}
                </div>
                <div className={`text-right font-semibold text-emerald-600 tabular-nums ${isDesktop ? 'w-28 text-sm' : 'w-20 text-[11px]'}`}>
                  {formatCurrency(poste.cout_global)}
                </div>
                
                {/* Heures */}
                <div className={`text-right text-gray-400 ${isDesktop ? 'w-20 text-xs' : 'w-14 text-[9px]'}`}>
                  {poste.heures_contrat || 35}h/sem
                </div>
              </div>
            );
          })}
          
          {/* Total global */}
          <div className={`flex items-center bg-gray-50 border-t border-gray-200 font-semibold ${isDesktop ? 'px-4 py-3' : 'px-2 py-2'}`}>
            <div className={`flex-1 text-gray-700 ${isDesktop ? 'text-sm' : 'text-[11px]'}`}>
              Total ({totaux.count} pers.)
            </div>
            <div className={isDesktop ? 'w-24' : 'w-16'}></div>
            <div className={`text-right text-gray-500 tabular-nums ${isDesktop ? 'w-24 text-xs' : 'w-16 text-[10px]'}`}>
              {formatCurrency(totaux.brut)}
            </div>
            <div className={`text-right text-emerald-600 tabular-nums ${isDesktop ? 'w-28 text-base' : 'w-20 text-xs'}`}>
              {formatCurrency(totaux.global)}
            </div>
            <div className={isDesktop ? 'w-20' : 'w-14'}></div>
          </div>
        </div>
      )}
    </div>
  );
};


// Composant DetailedSectionGroup pour la vue Boss (Golf détaillé)
const BossDetailedSection = ({ title, icon, yearData, prevYearData, selectedMonth, isDesktop, expandedSections, toggleSection, sectionKey }) => {
  if (!yearData) return null;
  const getVal = (item) => {
    if (!item) return 0;
    if (selectedMonth === 'cumul') return item.annual || 0;
    return item.monthly?.[selectedMonth] || 0;
  };
  const groups = [
    { key: 'ca', label: "Chiffre d'Affaires", isExpense: false },
    { key: 'salaires', label: 'Salaires', isExpense: true },
    { key: 'depenses', label: 'Autres Dépenses', isExpense: true }
  ];
  const resultatVal = yearData.resultat ? getVal(yearData.resultat) : 0;
  const prevResultatVal = prevYearData?.resultat ? getVal(prevYearData.resultat) : 0;
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className={`flex items-center gap-2 border-b bg-gray-50/80 ${isDesktop ? 'px-4 py-2.5' : 'px-3 py-2'}`}>
        {icon}
        <span className="font-bold text-gray-800 text-sm">{title}</span>
      </div>
      <div className="divide-y divide-gray-50">
        {groups.map(group => {
          const groupData = yearData[group.key];
          if (!groupData || (!groupData.items?.length && !groupData.subtotal)) return null;
          const subKey = `${sectionKey}_${group.key}`;
          const isExpanded = expandedSections.includes(subKey);
          const subtotalVal = groupData.subtotal ? getVal(groupData.subtotal) : 0;
          const prevSubtotalVal = prevYearData?.[group.key]?.subtotal ? getVal(prevYearData[group.key].subtotal) : 0;
          return (
            <div key={group.key}>
              <button onClick={() => toggleSection(subKey)}
                className={`w-full flex items-center ${isDesktop ? 'px-4 py-2' : 'px-3 py-1.5'} hover:bg-gray-50 transition-colors`}>
                <div className="flex items-center gap-1 min-w-0 flex-1">
                  {isExpanded ? <ChevronDown size={12} className="text-gray-400 shrink-0" /> : <ChevronRight size={12} className="text-gray-400 shrink-0" />}
                  <span className={`font-semibold truncate text-xs ${group.isExpense ? 'text-gray-700' : 'text-gray-800'}`}>
                    {groupData.subtotal?.label || group.label}
                  </span>
                </div>
                <div className={`shrink-0 flex justify-end ${isDesktop ? 'w-28' : 'w-[105px]'}`}>
                  <EvolutionBadge current={subtotalVal} previous={prevSubtotalVal} isExpense={group.isExpense} isDesktop={isDesktop} />
                </div>
                <span className={`shrink-0 text-right font-bold tabular-nums ${isDesktop ? 'w-28 text-sm' : 'w-[90px] text-sm'} ${group.isExpense ? 'text-red-600' : 'text-emerald-700'}`}>
                  {formatCurrency(subtotalVal)}
                </span>
              </button>
              {isExpanded && groupData.items?.map((item, idx) => {
                const val = getVal(item);
                const prevItems = prevYearData?.[group.key]?.items || [];
                const prevMatch = prevItems.find(i => i.label === item.label);
                const prevVal = prevMatch ? getVal(prevMatch) : 0;
                if (Math.abs(val) < 0.01 && Math.abs(prevVal) < 0.01) return null;
                return (
                  <div key={idx} className={`flex items-center ${isDesktop ? 'px-4 pl-10 py-1.5' : 'px-3 pl-8 py-1'} bg-gray-50/50`}>
                    <span className="text-gray-500 min-w-0 flex-1 truncate text-xs">{item.label}</span>
                    <div className={`shrink-0 flex justify-end ${isDesktop ? 'w-28' : 'w-[105px]'}`}>
                      <EvolutionBadge current={val} previous={prevVal} isExpense={group.isExpense} isDesktop={isDesktop} />
                    </div>
                    <span className={`shrink-0 text-right tabular-nums text-gray-600 ${isDesktop ? 'w-28 text-xs' : 'w-[90px] text-xs'}`}>{formatCurrency(val)}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
        {yearData.resultat && (
          <div className={`flex items-center ${isDesktop ? 'px-4 py-2.5' : 'px-3 py-2'}`} style={{ backgroundColor: resultatVal >= 0 ? '#f0fdf4' : '#fef2f2' }}>
            <span className="min-w-0 flex-1 truncate font-bold text-sm" style={{ color: resultatVal >= 0 ? '#166534' : '#991b1b' }}>Résultat</span>
            <div className={`shrink-0 flex justify-end ${isDesktop ? 'w-28' : 'w-[105px]'}`}>
              <EvolutionBadge current={resultatVal} previous={prevResultatVal} isNegativeValue={true} isDesktop={isDesktop} />
            </div>
            <span className={`shrink-0 text-right font-bold tabular-nums ${isDesktop ? 'w-28 text-sm' : 'w-[90px] text-sm'}`} style={{ color: resultatVal >= 0 ? '#166534' : '#991b1b' }}>
              {formatCurrency(resultatVal)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// PLSummary Boss
const BossPLSummary = ({ summaryData, prevSummaryData, isDesktop, expandedSections, toggleSection }) => {
  if (!summaryData || !summaryData.length) return null;
  const startKeys = ['ebe', 'autres_produits_charges', 'resultat_exploitation', 'resultat_financier', 'resultat_courant', 'resultat_exceptionnel', 'resultat_exercice'];
  const filteredData = summaryData.filter(i => startKeys.includes(i.key));
  if (!filteredData.length) return null;
  const getPrev = (key) => prevSummaryData?.find(i => i.key === key);
  const styleMap = {
    ebe: { bg: 'bg-violet-50', color: 'text-violet-800', border: 'border-violet-200', bold: true },
    resultat_exploitation: { bg: 'bg-amber-50', color: 'text-amber-800', border: 'border-amber-200', bold: true },
    resultat_courant: { bg: 'bg-orange-50', color: 'text-orange-800', border: 'border-orange-200', bold: true },
    resultat_exercice: { bg: 'bg-gray-900', color: 'text-white', border: 'border-gray-900', bold: true, dark: true }
  };
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="divide-y divide-gray-50">
        {filteredData.map(item => {
          const prevItem = getPrev(item.key);
          const prevVal = prevItem?.annual || 0;
          const style = styleMap[item.key] || {};
          const isGroup = item.type === 'group' && item.items?.length > 0;
          const subKey = `pl_${item.key}`;
          const isExpanded = expandedSections.includes(subKey);
          if (isGroup) {
            return (
              <div key={item.key}>
                <button onClick={() => toggleSection(subKey)} className={`w-full flex items-center ${isDesktop ? 'px-4 py-2' : 'px-3 py-1.5'} hover:bg-gray-50`}>
                  <div className="flex items-center gap-1 min-w-0 flex-1">
                    {isExpanded ? <ChevronDown size={12} className="text-gray-400 shrink-0" /> : <ChevronRight size={12} className="text-gray-400 shrink-0" />}
                    <span className="font-semibold truncate text-xs text-gray-700">{item.label}</span>
                  </div>
                  <div className={`shrink-0 flex justify-end ${isDesktop ? 'w-28' : 'w-[105px]'}`}>
                    <EvolutionBadge current={item.annual} previous={prevVal} isDesktop={isDesktop} />
                  </div>
                  <span className={`shrink-0 text-right font-bold tabular-nums ${isDesktop ? 'w-28 text-sm' : 'w-[90px] text-sm'} ${item.annual >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                    {formatCurrency(item.annual)}
                  </span>
                </button>
                {isExpanded && item.items.map((sub, idx) => (
                  <div key={idx} className={`flex items-center ${isDesktop ? 'px-4 pl-10 py-1.5' : 'px-3 pl-8 py-1'} bg-gray-50/50`}>
                    <span className="text-gray-500 min-w-0 flex-1 truncate text-xs">{sub.label}</span>
                    <div className={`shrink-0 flex justify-end ${isDesktop ? 'w-28' : 'w-[105px]'}`}>
                      <EvolutionBadge current={sub.annual} previous={prevItem?.items?.[idx]?.annual || 0} isDesktop={isDesktop} />
                    </div>
                    <span className={`shrink-0 text-right tabular-nums text-gray-600 ${isDesktop ? 'w-28 text-xs' : 'w-[90px] text-xs'}`}>{formatCurrency(sub.annual)}</span>
                  </div>
                ))}
              </div>
            );
          }
          return (
            <div key={item.key} className={`flex items-center ${isDesktop ? 'px-4 py-2.5' : 'px-3 py-2'} ${style.bg || ''} ${style.border ? `border-l-4 ${style.border}` : ''}`}>
              <span className={`min-w-0 flex-1 truncate ${style.bold ? 'font-bold' : 'font-semibold'} text-sm ${style.color || (item.annual >= 0 ? 'text-emerald-800' : 'text-red-800')}`}>{item.label}</span>
              <div className={`shrink-0 flex justify-end ${isDesktop ? 'w-28' : 'w-[105px]'}`}>
                {!style.dark && <EvolutionBadge current={item.annual} previous={prevVal} isNegativeValue={true} isDesktop={isDesktop} />}
              </div>
              <span className={`shrink-0 text-right ${style.bold ? 'font-bold' : 'font-semibold'} tabular-nums ${isDesktop ? 'w-28 text-sm' : 'w-[90px] text-sm'} ${style.dark ? 'text-white' : style.color || (item.annual >= 0 ? 'text-emerald-800' : 'text-red-800')}`}>{formatCurrency(item.annual)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Composant pour les marchés - Vue Boss
const ContractsBossView = ({ accessToken, isDesktop = false }) => {
  const [contracts, setContracts] = useState([]);
  const [totals, setTotals] = useState({});
  const [bureauEtudes, setBureauEtudes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/contracts/boss?token=${accessToken}`);
        if (res.ok) {
          const data = await res.json();
          setContracts(data.contracts || []);
          setTotals(data.totals || {});
          setBureauEtudes(data.bureau_etudes || null);
        }
      } catch (err) {
        console.error('Error fetching contracts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchContracts();
  }, [accessToken]);

  const filtered = useMemo(() => {
    if (!search) return contracts;
    const q = search.toLowerCase();
    return contracts.filter(c =>
      c.lot.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q) ||
      c.entreprise.toLowerCase().includes(q)
    );
  }, [contracts, search]);

  if (loading) return <div className="text-center py-8 text-neutral-500">Chargement...</div>;
  if (contracts.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-neutral-200">
        <Building2 size={48} className="mx-auto text-neutral-300 mb-3" />
        <h3 className="text-base font-semibold text-neutral-700 mb-1">Aucun marché</h3>
      </div>
    );
  }

  const globalPct = totals.montant_total > 0 ? Math.min((totals.montant_paye / totals.montant_total) * 100, 100) : 0;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="space-y-4">
        {/* === BLOC 1: Synthèse Travaux === */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
          <div
            className="p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
            onClick={() => setExpandedSection(expandedSection === 'travaux' ? null : 'travaux')}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Synthèse Travaux</span>
                {expandedSection === 'travaux' ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </div>
              <span className="text-base sm:text-lg font-bold text-blue-600 whitespace-nowrap ml-2">{formatCurrency(totals.montant_total)} <span className="text-[9px] sm:text-[10px] font-normal text-gray-400">Marchés signés</span></span>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3 text-center">
              <div>
                <div className="text-[10px] text-gray-400">Payé</div>
                <div className="text-sm font-semibold text-emerald-600">{formatCurrency(totals.montant_paye)}</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-400">Reste</div>
                <div className="text-sm font-semibold text-red-500">{formatCurrency((totals.montant_total || 0) - (totals.montant_paye || 0))}</div>
              </div>
            </div>
            <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
              <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 bg-emerald-500" style={{ width: `${globalPct}%` }} />
            </div>
            <div className="flex justify-between mt-1.5">
              <span className="text-xs text-gray-500 font-medium">{globalPct.toFixed(1)}% payé</span>
            </div>
          </div>

          {expandedSection === 'travaux' && (
            <div className="border-t border-gray-100 p-4">
              <div className="relative mb-3">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher lot, entreprise..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-emerald-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="space-y-2">
                {filtered.map((c) => {
                  const p = c.montant_total > 0 ? Math.min((c.montant_paye / c.montant_total) * 100, 100) : 0;
                  const isExpanded = expandedId === c.id;
                  return (
                    <div key={c.id} className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
                      <div className="px-3 py-2.5 cursor-pointer active:bg-gray-50" onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : c.id); }}>
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-gray-100 text-[10px] font-bold text-gray-600 shrink-0">{c.lot}</span>
                              <span className="text-xs font-medium text-gray-800 truncate">{c.description}</span>
                            </div>
                            <div className="text-[10px] text-blue-600 font-medium mt-0.5 truncate">{c.entreprise || '-'}</div>
                          </div>
                          <div className="text-right shrink-0 flex items-center gap-1">
                            {c.montant_total === 0 && c.montant_initial === 0 ? (
                              <span className="text-[10px] font-medium text-orange-500 italic">Marché pas encore signé</span>
                            ) : (
                              <div>
                                <div className="text-xs font-semibold text-gray-700">{formatCurrency(c.montant_total)}</div>
                                <div className="text-[10px] text-emerald-600 font-medium">{formatCurrency(c.montant_paye)} payé</div>
                              </div>
                            )}
                            {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                          </div>
                        </div>
                        {c.montant_total > 0 && (
                          <>
                            <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 bg-emerald-500" style={{ width: `${p}%` }} />
                            </div>
                            <div className="flex justify-between mt-0.5">
                              <span className="text-[9px] text-gray-400">{p.toFixed(1)}%</span>
                            </div>
                          </>
                        )}
                      </div>
                      {isExpanded && (
                        <div className="border-t border-gray-100 px-3 py-3 bg-gray-50/50">
                          <div className="grid grid-cols-2 gap-2 text-[11px]">
                            <div>
                              <span className="text-gray-400">Marché initial</span>
                              <div className="font-semibold text-gray-700">{formatCurrency(c.montant_initial)}</div>
                            </div>
                            <div>
                              <span className="text-gray-400">Avenants</span>
                              <div className={`font-semibold ${c.montant_avenants > 0 ? 'text-amber-600' : 'text-gray-500'}`}>{formatCurrency(c.montant_avenants)}</div>
                            </div>
                            <div>
                              <span className="text-gray-400">Montant total</span>
                              <div className="font-semibold text-gray-700">{formatCurrency(c.montant_total)}</div>
                            </div>
                            <div>
                              <span className="text-gray-400">Reste à payer</span>
                              <div className="font-semibold text-red-500">{formatCurrency(c.montant_total - c.montant_paye)}</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 text-center text-[10px] text-gray-400">
                {filtered.length} lot{filtered.length > 1 ? 's' : ''}{search && ` sur ${contracts.length}`}
              </div>
            </div>
          )}
        </div>

        {/* === BLOC 2: Synthèse Bureau d'étude === */}
        {bureauEtudes && bureauEtudes.entries && bureauEtudes.entries.length > 0 && (() => {
          const bdeGlobalPct = bureauEtudes.total > 0 ? (bureauEtudes.total_paye / bureauEtudes.total * 100) : 0;
          return (
            <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
              <div
                className="p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
                onClick={() => setExpandedSection(expandedSection === 'bde' ? null : 'bde')}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Synthèse Bureau d'étude</span>
                    {expandedSection === 'bde' ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </div>
                  <span className="text-base sm:text-lg font-bold text-indigo-600 whitespace-nowrap ml-2">{formatCurrency(bureauEtudes.total)} <span className="text-[9px] sm:text-[10px] font-normal text-gray-400">Marchés signés</span></span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3 text-center">
                  <div>
                    <div className="text-[10px] text-gray-400">Payé</div>
                    <div className="text-sm font-semibold text-emerald-600">{formatCurrency(bureauEtudes.total_paye)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400">Reste</div>
                    <div className="text-sm font-semibold text-red-500">{formatCurrency(bureauEtudes.total - bureauEtudes.total_paye)}</div>
                  </div>
                </div>
                <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 bg-emerald-500" style={{ width: `${bdeGlobalPct}%` }} />
                </div>
                <div className="flex justify-between mt-1.5">
                  <span className="text-xs text-gray-500 font-medium">{bdeGlobalPct.toFixed(1)}% payé</span>
                </div>
              </div>

              {expandedSection === 'bde' && (
                <div className="border-t border-gray-100 p-4 space-y-1.5">
                  {bureauEtudes.entries.map((e) => {
                    const bPct = e.montant > 0 ? (e.montant_paye / e.montant * 100) : 0;
                    return (
                      <div key={e.id} className="px-2.5 py-2 rounded-lg border border-gray-50 hover:bg-gray-50/50 transition-colors">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium text-gray-800 truncate">{e.description}</div>
                            <div className="text-[10px] text-indigo-500 font-medium">{e.entreprise || '-'}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-xs font-semibold text-gray-700">{formatCurrency(e.montant)}</div>
                            <div className="text-[10px] text-emerald-600 font-medium">{formatCurrency(e.montant_paye)} payé</div>
                          </div>
                        </div>
                        <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 bg-emerald-500" style={{ width: `${Math.min(bPct, 100)}%` }} />
                        </div>
                        <div className="flex justify-between mt-0.5">
                          <span className="text-[9px] text-gray-400">{bPct.toFixed(1)}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

// Options de mois
const monthOptions = [
  { value: 'cumul', label: 'Cumul annuel' },
  { value: '01', label: 'Janvier' }, { value: '02', label: 'Février' },
  { value: '03', label: 'Mars' }, { value: '04', label: 'Avril' },
  { value: '05', label: 'Mai' }, { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' }, { value: '08', label: 'Août' },
  { value: '09', label: 'Septembre' }, { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' }, { value: '12', label: 'Décembre' }
];

const ResultsBossView = () => {
  const [searchParams] = useSearchParams();
  const { code } = useParams();
  const accessToken = code || searchParams.get('token');
  
  const [activeTab, setActiveTab] = useState('golf');
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState('cumul');
  const [selectedMonthRestaurant, setSelectedMonthRestaurant] = useState('cumul');
  const [yearInitialized, setYearInitialized] = useState(false);
  const [hideZeros, setHideZeros] = useState(true);
  
  // Detect desktop mode
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const [golfData, setGolfData] = useState(null);
  const [golfDetailData, setGolfDetailData] = useState(null);
  const [restaurantData, setRestaurantData] = useState(null);
  const [comments, setComments] = useState({});
  const [orgInfo, setOrgInfo] = useState({ name: 'Golf', logo_url: null, portal_logo: null });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Popup commentaire
  const [commentPopup, setCommentPopup] = useState(null);
  const [commentEditText, setCommentEditText] = useState('');
  
  // Analyse globale
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [globalAnalysis, setGlobalAnalysis] = useState('');
  
  // Sections expandables
  const [expandedSections, setExpandedSections] = useState([]);
  const toggleSection = useCallback((key) => {
    setExpandedSections(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  }, []);
  // Charger les données
  const loadData = useCallback(async () => {
    if (!accessToken) {
      setError('Token manquant');
      setLoading(false);
      return;
    }
    
    try {
      const [golfRes, golfDetailRes, restaurantRes, commentsRes, orgRes] = await Promise.all([
        fetch(`${API_URL}/api/results/data/boss?access_token=${accessToken}`),
        fetch(`${API_URL}/api/results/golf-detail/data/boss?access_token=${accessToken}`),
        fetch(`${API_URL}/api/restaurant-results/boss/data?token=${accessToken}`),
        fetch(`${API_URL}/api/results/comments/public?access_token=${accessToken}`),
        fetch(`${API_URL}/api/results/organization/public?access_token=${accessToken}`)
      ]);
      
      if (golfRes.ok) {
        const data = await golfRes.json();
        setGolfData(data.data);
      }
      
      if (golfDetailRes.ok) {
        const data = await golfDetailRes.json();
        setGolfDetailData(data.data);
      }
      
      if (restaurantRes.ok) {
        const data = await restaurantRes.json();
        setRestaurantData(data.data);
      }
      
      if (commentsRes.ok) {
        const data = await commentsRes.json();
        setComments(data.comments || {});
      }
      
      if (orgRes.ok) {
        const data = await orgRes.json();
        setOrgInfo(data);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);
  
  // Charger l'analyse globale
  const loadGlobalAnalysis = useCallback(async () => {
    if (!accessToken) return;
    try {
      const endpoint = activeTab === 'golf' 
        ? `${API_URL}/api/results/global-analysis/public?access_token=${accessToken}`
        : `${API_URL}/api/restaurant-results/global-analysis/public?token=${accessToken}`;
      const res = await fetch(endpoint);
      if (res.ok) {
        const data = await res.json();
        setGlobalAnalysis(data.analysis || '');
      }
    } catch (error) {
      console.error('Error loading analysis:', error);
    }
  }, [accessToken, activeTab]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Auto-sélectionner l'année la plus récente à l'ouverture
  useEffect(() => {
    if (!yearInitialized) {
      const allYears = new Set();
      const extractYears = (sections, keyName) => {
        if (!sections) return;
        sections.forEach(s => {
          const obj = s.subtotal?.[keyName] || {};
          Object.keys(obj).forEach(y => allYears.add(y));
        });
      };
      extractYears(golfData?.sections, 'cumul');
      extractYears(restaurantData?.sections, 'yearly');
      extractYears(restaurantData?.sections, 'cumul');
      if (allYears.size > 0) {
        const maxYear = [...allYears].sort().pop();
        setSelectedYear(maxYear);
        setYearInitialized(true);
      }
    }
  }, [golfData, restaurantData, yearInitialized]);

  // Années disponibles dynamiques
  const availableYears = useMemo(() => {
    const years = new Set();
    const extractYears = (sections, keyName) => {
      if (!sections) return;
      sections.forEach(s => {
        const obj = s.subtotal?.[keyName] || {};
        Object.keys(obj).forEach(y => years.add(y));
      });
    };
    extractYears(golfData?.sections, 'cumul');
    extractYears(restaurantData?.sections, 'yearly');
    extractYears(restaurantData?.sections, 'cumul');
    years.add(String(new Date().getFullYear()));
    return [...years].sort().reverse();
  }, [golfData, restaurantData]);

  useEffect(() => {
    loadGlobalAnalysis();
  }, [loadGlobalAnalysis]);
  
  // Ouvrir le popup de commentaire
  const openCommentPopup = (sectionKey, sectionTitle, commentKey, existingText) => {
    setCommentPopup({ sectionKey, sectionTitle, commentKey });
    setCommentEditText(existingText || '');
  };

  // Fermer le popup de commentaire
  const closeCommentPopup = () => {
    setCommentPopup(null);
    setCommentEditText('');
  };

  // Sauvegarder depuis le popup
  const saveCommentFromPopup = async () => {
    if (!commentPopup) return;
    
    try {
      const res = await fetch(`${API_URL}/api/results/boss-comments?access_token=${accessToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section_key: commentPopup.sectionKey,
          month: commentPopup.commentKey.split('_').pop(),
          text: commentEditText
        })
      });

      if (res.ok) {
        loadData();
        closeCommentPopup();
      }
    } catch (error) {
      console.error('Error saving comment:', error);
    }
  };
  
  // Sections filtrées Golf
  const golfSections = useMemo(() => {
    if (!golfData?.sections) return { proshop: [], taylorMade: [], golf: [], others: [] };
    const sections = golfData.sections.filter(s => {
      if (hiddenSections.includes(s.key)) return false;
      if (hideIfZeroSections.includes(s.key)) {
        const total = selectedMonth === 'cumul' 
          ? (s.subtotal?.cumul?.[selectedYear] || 0)
          : (s.subtotal?.monthly?.[`${selectedYear}-${selectedMonth}`] || 0);
        if (Math.abs(total) < 0.01) return false;
      }
      return true;
    });
    
    const proshop = sections.filter(s => s.key.startsWith('proshop'));
    const taylorMade = sections.filter(s => s.key.startsWith('taylor_made'));
    const golf = sections.filter(s => ['ca_golf', 'matieres', 'consommations', 'taxes', 'personnel'].includes(s.key));
    const others = sections.filter(s => 
      !s.key.startsWith('proshop') && 
      !s.key.startsWith('taylor_made') && 
      !['ca_golf', 'matieres', 'consommations', 'taxes', 'personnel'].includes(s.key)
    );
    
    return { proshop, taylorMade, golf, others };
  }, [golfData, selectedYear, selectedMonth]);
  
  // Sections filtrées Restaurant
  const restaurantSections = useMemo(() => {
    if (!restaurantData?.sections) return { restaurant: [], others: [] };
    const sections = restaurantData.sections.filter(s => {
      if (hiddenSections.includes(s.key)) return false;
      if (hideIfZeroSections.includes(s.key)) {
        const total = s.subtotal?.yearly?.[selectedYear] || s.subtotal?.cumul?.[selectedYear] || 0;
        if (Math.abs(total) < 0.01) return false;
      }
      return true;
    });
    
    const restaurant = sections.filter(s => 
      s.key.startsWith('ca_restaurant') || 
      s.key === 'cout_marchandises' || 
      s.key === 'marge_commerciale'
    );
    const others = sections.filter(s => 
      !s.key.startsWith('ca_restaurant') && 
      !s.key.startsWith('ca_hotel') &&
      s.key !== 'cout_marchandises' &&
      s.key !== 'marge_commerciale' &&
      s.key !== 'cout_matieres' &&
      s.key !== 'marge_production'
    );
    
    return { restaurant, others };
  }, [restaurantData, selectedYear]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-200 border-t-emerald-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 text-sm">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-3xl shadow-xl text-center max-w-sm">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Accès refusé</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }
  
  const tabs = [
    { id: 'golf', label: 'Golf', icon: Euro },
    { id: 'restaurant', label: 'Restaurant', icon: Utensils },
    { id: 'salaires', label: 'Salaires', icon: Users },
    { id: 'marches', label: 'Marchés', icon: Building2 }
  ];
  
  const commonPropsGolf = {
    selectedYear,
    selectedMonth,
    hideZeros,
    comments,
    isGolf: true,
    allSections: golfData?.sections || [],
    isDesktop
  };
  
  const commonPropsRestaurant = {
    selectedYear,
    selectedMonth: selectedMonthRestaurant,
    hideZeros,
    comments,
    isGolf: false,
    allSections: restaurantData?.sections || [],
    isDesktop
  };
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="boss-view-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
        <div className="max-w-6xl mx-auto px-4 py-3 pt-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              {orgInfo.portal_logo?.data ? (
                <img 
                  src={`data:${orgInfo.portal_logo.content_type};base64,${orgInfo.portal_logo.data}`}
                  alt={orgInfo.name}
                  className="w-12 h-12 rounded-xl object-contain"
                />
              ) : orgInfo.logo_url ? (
                <img 
                  src={orgInfo.logo_url} 
                  alt={orgInfo.name}
                  className="w-12 h-12 rounded-xl object-cover shadow-lg"
                />
              ) : (
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Building2 size={24} className="text-white" />
                </div>
              )}
              <div>
                <h1 className="text-lg font-bold text-gray-800">
                  Résultats {activeTab === 'golf' ? 'Golf' : activeTab === 'restaurant' ? 'Restaurant' : activeTab === 'marches' ? 'Marchés' : 'Salaires'}
                </h1>
                <p className="text-xs text-gray-500">Chiffres Kempf pour PEB</p>
              </div>
            </div>
          </div>
          
          {/* Onglets */}
          <div className="flex gap-0.5 bg-gray-100 p-1 rounded-xl mb-3 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-white text-emerald-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Icon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>
          
          {/* Filtres */}
          {activeTab !== 'salaires' && activeTab !== 'marches' && (
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-gray-400" />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-2 py-1 border border-gray-200 rounded text-xs font-medium focus:ring-1 focus:ring-emerald-500"
              >
                {availableYears.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              
              {/* Filtre mois pour Golf */}
              {activeTab === 'golf' && (
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="px-2 py-1 border border-gray-200 rounded text-xs font-medium focus:ring-1 focus:ring-emerald-500"
                >
                  {monthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              )}
              
              {/* Filtre mois pour Restaurant */}
              {activeTab === 'restaurant' && (
                <select
                  value={selectedMonthRestaurant}
                  onChange={(e) => setSelectedMonthRestaurant(e.target.value)}
                  className="px-2 py-1 border border-gray-200 rounded text-xs font-medium focus:ring-1 focus:ring-emerald-500"
                >
                  {monthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              )}
              
              {/* Bouton Analyse d'Arnaud - Rectangulaire avec bords arrondis */}
              <button
                onClick={() => setShowAnalysis(true)}
                className="ml-auto mr-2 px-3 py-2 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-lg flex items-center gap-2 hover:scale-105 active:scale-95"
                title="Analyse d'Arnaud"
              >
                <Lightbulb size={16} className="fill-white" />
                <span className="text-xs font-semibold">Analyse d'Arnaud</span>
              </button>
            </div>
          )}
        </div>
      </header>
      
      {/* Popup Commentaire */}
      {commentPopup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 text-sm">{commentPopup.sectionTitle}</h3>
              <button onClick={closeCommentPopup} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            <textarea
              value={commentEditText}
              onChange={(e) => setCommentEditText(e.target.value)}
              placeholder="Votre commentaire..."
              className="w-full h-32 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              autoFocus
            />
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                onClick={closeCommentPopup}
                className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
              >
                Annuler
              </button>
              <button
                onClick={saveCommentFromPopup}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Popup Analyse Globale */}
      {showAnalysis && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800">Analyse d'Arnaud</h3>
              <button onClick={() => setShowAnalysis(false)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            {globalAnalysis ? (
              <div className="flex-1 overflow-auto">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{globalAnalysis}</p>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">Aucune analyse disponible.</p>
            )}
            <div className="flex items-center justify-end mt-3">
              <button
                onClick={() => setShowAnalysis(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Contenu */}
      <main className="max-w-6xl mx-auto px-4 py-4">
        {/* Golf */}
        {activeTab === 'golf' && (
          golfDetailData ? (
            <div className={isDesktop ? 'space-y-4' : 'space-y-3'}>
              {golfDetailData.boutique?.[selectedYear] && (
                <BossDetailedSection
                  title="Pro Shop"
                  icon={<div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-[11px]">PS</div>}
                  yearData={golfDetailData.boutique[selectedYear]}
                  prevYearData={golfDetailData.boutique[String(parseInt(selectedYear)-1)]}
                  selectedMonth={selectedMonth}
                  isDesktop={isDesktop}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  sectionKey="ps"
                />
              )}
              {golfDetailData.taylor_made?.[selectedYear] && (
                <BossDetailedSection
                  title="Taylor Made"
                  icon={<div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-[11px]">TM</div>}
                  yearData={golfDetailData.taylor_made[selectedYear]}
                  prevYearData={golfDetailData.taylor_made[String(parseInt(selectedYear)-1)]}
                  selectedMonth={selectedMonth}
                  isDesktop={isDesktop}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  sectionKey="tm"
                />
              )}
              {golfDetailData.golf?.[selectedYear] && (
                <BossDetailedSection
                  title="Golf"
                  icon={<div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-[11px]">G</div>}
                  yearData={golfDetailData.golf[selectedYear]}
                  prevYearData={golfDetailData.golf[String(parseInt(selectedYear)-1)]}
                  selectedMonth={selectedMonth}
                  isDesktop={isDesktop}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  sectionKey="golf"
                />
              )}
              {golfDetailData.academie?.[selectedYear] && (
                <BossDetailedSection
                  title="Académie"
                  icon={<div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 font-bold text-[11px]">A</div>}
                  yearData={golfDetailData.academie[selectedYear]}
                  prevYearData={golfDetailData.academie[String(parseInt(selectedYear)-1)]}
                  selectedMonth={selectedMonth}
                  isDesktop={isDesktop}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  sectionKey="acad"
                />
              )}
              {selectedMonth === 'cumul' && golfDetailData.pl_summary?.[selectedYear] && (
                <BossPLSummary
                  summaryData={golfDetailData.pl_summary[selectedYear]}
                  prevSummaryData={golfDetailData.pl_summary[String(parseInt(selectedYear)-1)]}
                  isDesktop={isDesktop}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                />
              )}
            </div>
          ) : (
            <div className={isDesktop ? 'space-y-4' : 'space-y-3'}>
              {golfSections.proshop.length > 0 && (
                <SectionGroup
                  title="Pro Shop"
                  icon={<div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-[11px]">PS</div>}
                  sections={golfSections.proshop}
                  {...commonPropsGolf}
                />
              )}
              {golfSections.taylorMade.length > 0 && (
                <SectionGroup
                  title="Taylor Made"
                  icon={<div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-[11px]">TM</div>}
                  sections={golfSections.taylorMade}
                  {...commonPropsGolf}
                />
              )}
              {golfSections.golf.length > 0 && (
                <SectionGroup
                  title="Golf"
                  icon={<div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-[11px]">G</div>}
                  sections={golfSections.golf}
                  {...commonPropsGolf}
                />
              )}
              {golfSections.others.map((section) => (
                <SectionAccordion key={section.key} section={section} {...commonPropsGolf} />
              ))}
            </div>
          )
        )}
        
        {/* Restaurant */}
        {activeTab === 'restaurant' && (
          <div className={isDesktop ? 'space-y-4' : 'space-y-3'}>
            {restaurantSections.restaurant.length > 0 && (
              <SectionGroup
                title="Restaurant"
                icon={<div className={`${isDesktop ? 'w-7 h-7 text-xs' : 'w-5 h-5 text-[10px]'} bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold`}>R</div>}
                sections={restaurantSections.restaurant}
                {...commonPropsRestaurant}
              />
            )}
            
            {restaurantSections.others.map((section) => (
              <SectionAccordion
                key={section.key}
                section={section}
                {...commonPropsRestaurant}
              />
            ))}
          </div>
        )}
        
        {/* Salaires */}
        {activeTab === 'salaires' && (
          <SalairesView accessToken={accessToken} isDesktop={isDesktop} />
        )}
        
        {/* Marchés */}
        {activeTab === 'marches' && (
          <ContractsBossView accessToken={accessToken} isDesktop={isDesktop} />
        )}
      </main>
      
      {/* Footer */}
      <footer className="text-center py-4 text-xs text-gray-400">
        Chiffres Kempf pour PEB • Lien permanent
      </footer>
    </div>
  );
};

export default ResultsBossView;
